import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { buildAlternateLinks, buildPageUrl, DEFAULT_META_IMAGE, ORGANIZATION_LD, SITE_NAME, SUPPORTED_LOCALES, toAbsoluteUrl } from '../config/seo';

function ensureArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

export default function SeoMeta({
  title,
  description,
  keywords,
  image = DEFAULT_META_IMAGE,
  path = '/',
  url,
  alternates,
  structuredData,
  noIndex = false,
}) {
  const { i18n } = useTranslation();
  const langCode = (i18n.resolvedLanguage || i18n.language || 'ua').slice(0, 2);
  const localeInfo = SUPPORTED_LOCALES.find((item) => item.code === langCode) || SUPPORTED_LOCALES[0];

  const canonicalUrl = url || buildPageUrl(path, localeInfo.code);
  const hreflangs = alternates || buildAlternateLinks(path);
  const imageUrl = toAbsoluteUrl(image);
  const scripts = [ORGANIZATION_LD, ...ensureArray(structuredData)].filter(Boolean);

  return (
    <Helmet>
      <html lang={localeInfo.hrefLang?.split('-')[0] || localeInfo.code} />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}

      <meta property="og:type" content="website" />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={localeInfo.hrefLang?.replace('-', '_') || 'uk_UA'} />

      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {hreflangs.map(({ hrefLang, href }) => (
        <link key={`${hrefLang}-${href}`} rel="alternate" hrefLang={hrefLang} href={href} />
      ))}

      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {scripts.map((item, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
